import * as React from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Markdown from "react-markdown";
import { ListItem } from "@mui/material";

const markdown = `
# Privacy Policy

## 1. Introduction
Welcome to **Let's Quit Drinking Together** ("the App"). Your privacy is important to us, and this Privacy Policy explains how we collect, use, and protect your information.

## 2. Information We Collect
We collect the following types of information:
- **Personal Information**: Email address, profile information (if provided).
- **App Usage Data**: Interaction data, journaling activity, and community engagement.
- **Device Information**: Device type, operating system, and app version.

## 3. How We Use Your Information
We use the information collected to:
- Provide and improve the App's functionality.
- Customize user experience.
- Facilitate community interactions and support.

## 4. Data Sharing
We do not sell or share your personal information with third parties, except:
- When required by law.
- To protect the rights or safety of our users.
- With trusted service providers who assist in maintaining the App.

## 5. Anonymity and User Control
- Accounts are set to anonymous by default.
- Users can choose to update anonymity settings via the profile options.
- Journal entries and sensitive data remain private and are not shared publicly.

## 6. Data Retention
- User data is retained as long as your account is active.
- Upon account deletion, all associated data is permanently removed within 7-10 business days unless otherwise requested i.e. Data can be deleted sooner by request or can be kept in case you want to come back at a later date.

## 7. Security Measures
We implement industry-standard security measures to protect your data, including encryption and secure data storage. However, no system is entirely foolproof, and we cannot guarantee absolute security.

## 8. Children's Privacy
The App is not intended for individuals under 18 years old. We do not knowingly collect information from minors.

## 9. Changes to This Policy
We may update this Privacy Policy from time to time. Changes will be effective upon posting to the App. Continued use of the App constitutes acceptance of the updated policy.

## 10. Contact Us
For questions or concerns regarding this Privacy Policy, please contact us at [support@knitbucket.com](mailto:support@knitbucket.com).
`;

export default function PrivacyContent() {
	return (
		<Container
			id="privacy"
			sx={{
				pt: { xs: 25, sm: 25 },
				pb: { xs: 8, sm: 16 },
				position: "relative",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				gap: { xs: 3, sm: 6 },
			}}
		>
			<Grid
				container
				spacing={3}
				sx={{
					width: "100%",
				}}
			>
				<Markdown
					components={{
						// Use h2s instead of h1s
						h1(props) {
							const { node, ...rest } = props;
							return (
								<Grid item container>
									<Grid item>
										<Typography variant="h1" {...rest} />
									</Grid>
								</Grid>
							);
						},
						h2(props) {
							const { node, ...rest } = props;
							return (
								<Grid item container>
									<Grid item>
										<Typography variant="h2" {...rest} />
									</Grid>
								</Grid>
							);
						},
						h3(props) {
							const { node, ...rest } = props;
							return (
								<Grid item container>
									<Grid item>
										<Typography variant="h3" {...rest} />
									</Grid>
								</Grid>
							);
						},
						h4(props) {
							const { node, ...rest } = props;
							return (
								<Grid item container>
									<Grid item>
										<Typography variant="h4" {...rest} />
									</Grid>
								</Grid>
							);
						},
						h5(props) {
							const { node, ...rest } = props;
							return (
								<Grid item container>
									<Grid item>
										<Typography variant="h5" {...rest} />
									</Grid>
								</Grid>
							);
						},
						h6(props) {
							const { node, ...rest } = props;
							return (
								<Grid item container>
									<Grid item>
										<Typography variant="h6" {...rest} />
									</Grid>
								</Grid>
							);
						},
						p(props) {
							const { node, ...rest } = props;
							return (
								<Grid item container>
									<Grid item>
										<Typography variant="body1" {...rest} />
									</Grid>
								</Grid>
							);
						},
						li(props) {
							const { node, children, ...rest } = props;
							return (
								<Grid item container alignItems={"center"}>
									<Grid item xs={false}>
										&bull;
									</Grid>
									<Grid item xs>
										<ListItem variant="body1" {...rest}>
											<Typography variant="body1">
												{children}
											</Typography>
										</ListItem>
									</Grid>
								</Grid>
							);
						},
					}}
				>
					{markdown}
				</Markdown>
			</Grid>
		</Container>
	);
}
